import React, { CSSProperties, forwardRef } from 'react';
import { Link as RouterLink, LinkProps as RouterLinkProps } from 'react-router-dom';
import { LinkProps } from '@mui/material/Link';
import { createTheme } from '@mui/material/styles';
import {
  basic_action_button,
  basic_action_button_disabled,
  secondary_action_button,
  secondary_action_button_disabled,
} from 'new/theme.buttons';

export const interpolateCSSVariable = (name: string) => {
  return getComputedStyle(window.document.body).getPropertyValue(name)?.trim();
};

const LinkBehavior = forwardRef<
  HTMLAnchorElement,
  Omit<RouterLinkProps, 'to'> & { href: RouterLinkProps['to'] }
>((props, ref) => {
  const { href, ...other } = props;
  // Map href (MUI) -> to (react-router)
  return <RouterLink ref={ref} to={href} {...other} />;
});

const body2FontProps = {
  fontSize: 14,
  fontWeight: 400,
  lineHeight: '20px',
};

function getBaseGhostProps(theme, color = 'primary') {
  return {
    backgroundColor: theme.palette[color].light50,
    border: `1px solid ${theme.palette[color].light200}`,
    color: theme.palette[color].main,
    '&:hover': {
      border: `1px solid ${theme.palette[color].light200}`,
      backgroundColor: theme.palette[color || 'primary'].light100,
      color: theme.palette[color].main,
    },
  };
}

function makeFinalTheme() {
  return createTheme({
    typography: {
      fontFamily: ['var(--first-font-family)', 'var(--second-font-family)'].join(','),
      subheading2: {
        fontSize: 'var(--font-size-base)',
        lineHeight: 'var(--line-height-base)',
        fontWeight: 'var(--font-weight-medium)',
      },
      subtitle1: {
        fontSize: 16,
        fontWeight: 500,
        lineHeight: 'var(--line-height-base)',
      },
      subtitle2: {
        fontSize: 14,
        fontWeight: 500,
        lineHeight: 'var(--line-height-base)',
      },

      h1: {
        fontSize: 34,
        fontWeight: 400,
        lineHeight: '40px',
        color: 'var(--color-greyscale-1000)',
      },
      h2: {
        fontSize: 24,
        fontWeight: 400,
        lineHeight: 'var(--line-height-xxl)',
        color: 'var(--color-greyscale-1000)',
      },
      h3: {
        fontSize: 20,
        fontWeight: 400,
        lineHeight: '24px',
        color: 'var(--color-greyscale-1000)',
      },
      h4: {
        fontSize: 48,
        fontWeight: 700,
        lineHeight: '50px',
        color: 'var(--color-greyscale-1000)',
      },
      h5: {
        fontSize: 20,
        fontWeight: 500,
        lineHeight: '24px',
      },
      h6: {
        fontSize: 14,
        fontWeight: 700,
        lineHeight: 'var(--line-height-base)',
      },

      body1: {
        fontSize: 14,
        fontWeight: 400,
        lineHeight: '24px',
        color: 'var(--color-greyscale-800)',
      },
      body2: {
        ...body2FontProps,
        color: 'var(--color-greyscale-800)',
      },
      body3: {
        fontSize: 12,
        fontWeight: 400,
        lineHeight: 'var(--line-height-sm)',
        color: 'var(--color-greyscale-800)',
      },
      bodySmall: {
        fontSize: 12,
        lineHeight: 'var(--line-height-sm)',
      },
      bodyLarge: {
        fontSize: 16,
        lineHeight: '24px',
      },
      bodyDefault: {
        fontSize: 14,
        fontWeight: 400,
        lineHeight: 'var(--line-height-base)',
        fontStyle: 'normal',
      },

      longform: {
        fontSize: 16,
        fontWeight: 400,
        lineHeight: '28px',
      },
      subheading: {
        fontSize: 16,
        fontWeight: 500,
        lineHeight: 'var(--line-height-base)',
        color: 'var(--color-greyscale-1000)',
      },
      longForm: {
        fontSize: 16,
        fontWeight: 400,
        lineHeight: '28px',
        color: 'var(--color-greyscale-600)',
      },
      button: {
        fontSize: 16,
        fontWeight: 500,
        lineHeight: 'var(--line-height-base)',
        color: 'var(--color-greyscale-1000)',
      },
      button2: {
        fontSize: 14,
        fontWeight: 500,
        lineHeight: 'var(--line-height-base)',
        color: 'var(--color-greyscale-1000)',
      },
      caption: {
        fontSize: 12,
        fontWeight: 500,
        lineHeight: 'var(--line-height-sm)',
      },
      overline: {
        fontSize: 12,
        fontWeight: 400,
        lineHeight: 'var(--line-height-sm)',
      },
      bold: {
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 'var(--line-height-sm)',
      },
      tiny: {
        fontSize: 10,
        fontWeight: 400,
        lineHeight: 'var(--line-height-sm)',
      },
      menu2: {
        fontSize: 8,
        fontWeight: 500,
        lineHeight: 'var(--line-height-sm)',
        color: 'var(--color-primary-100)',
      },
    },
    palette: {
      mode: 'light',
      primary: {
        light: interpolateCSSVariable('--color-primary-100'),
        light50: interpolateCSSVariable('--color-primary-50'),
        light100: interpolateCSSVariable('--color-primary-100'),
        light200: interpolateCSSVariable('--color-primary-200'),
        main: interpolateCSSVariable('--color-primary-600'),
        dark: interpolateCSSVariable('--color-primary-900'),
        contrastText: interpolateCSSVariable('--color-greyscale-0'),
      },
      secondary: {
        light: interpolateCSSVariable('--color-secondary-200'),
        light50: interpolateCSSVariable('--color-secondary-50'),
        light100: interpolateCSSVariable('--color-secondary-100'),
        light200: interpolateCSSVariable('--color-secondary-200'),
        main: interpolateCSSVariable('--color-secondary-500'),
        dark: interpolateCSSVariable('--color-secondary-900'),
        contrastText: interpolateCSSVariable('--color-greyscale-100'),
      },
      disabled: {
        light: interpolateCSSVariable('--color-greyscale-100'),
        light50: interpolateCSSVariable('--color-greyscale-50'),
        light100: interpolateCSSVariable('--color-greyscale-100'),
        light200: interpolateCSSVariable('--color-greyscale-200'),
        main: interpolateCSSVariable('--color-greyscale-600'),
        dark: interpolateCSSVariable('--color-greyscale-800'),
        contrastText: interpolateCSSVariable('--color-text-disabled'),
      },
      error: {
        light: interpolateCSSVariable('--color-danger-200'),
        light50: interpolateCSSVariable('--color-danger-50'),
        light100: interpolateCSSVariable('--color-danger-100'),
        light200: interpolateCSSVariable('--color-danger-400'),
        main: interpolateCSSVariable('--color-danger-700'),
        dark: interpolateCSSVariable('--color-danger-900'),
      },
      success: {
        light: interpolateCSSVariable('--color-success-200'),
        light50: interpolateCSSVariable('--color-success-50'),
        light100: interpolateCSSVariable('--color-success-100'),
        light200: interpolateCSSVariable('--color-success-400'),
        main: interpolateCSSVariable('--color-success-700'),
        dark: interpolateCSSVariable('--color-secondary-900'),
      },
      warning: {
        light: interpolateCSSVariable('--color-success-200'),
        light50: interpolateCSSVariable('--color-success-50'),
        light100: interpolateCSSVariable('--color-success-100'),
        light200: interpolateCSSVariable('--color-success-400'),
        main: interpolateCSSVariable('--color-warning-500'),
        dark: interpolateCSSVariable('--color-warning-900'),
        contrastText: interpolateCSSVariable('--color-greyscale-800'),
      },
      info: {
        main: interpolateCSSVariable('--color-greyscale-50'),
        light: interpolateCSSVariable('--color-greyscale-100'),
        light50: interpolateCSSVariable('--color-greyscale-50'),
        light100: interpolateCSSVariable('--color-greyscale-100'),
        light200: interpolateCSSVariable('--color-greyscale-200'),
        contrastText: interpolateCSSVariable('--color-greyscale-800'),
      },
      neutral: {
        light: interpolateCSSVariable('--color-greyscale-50'),
        light50: interpolateCSSVariable('--color-greyscale-50'),
        light100: interpolateCSSVariable('--color-greyscale-100'),
        light200: interpolateCSSVariable('--color-greyscale-100'),
        grey100: interpolateCSSVariable('--color-greyscale-100'),
        grey600: interpolateCSSVariable('--color-greyscale-600'),
        main: interpolateCSSVariable('--color-greyscale-600'),
        dark: interpolateCSSVariable('--color-greyscale-900'),
      },
      basic: {
        light: interpolateCSSVariable('--color-greyscale-50'),
        light50: interpolateCSSVariable('--color-greyscale-50'),
        light100: interpolateCSSVariable('--color-greyscale-100'),
        light200: interpolateCSSVariable('--color-greyscale-100'),
        main: interpolateCSSVariable('--color-greyscale-600'),
        dark: interpolateCSSVariable('--color-greyscale-900'),
        contrastText: interpolateCSSVariable('--color-greyscale-100'),
      },
      text: {
        primary: interpolateCSSVariable(`--color-greyscale-1000`),
        secondary: interpolateCSSVariable(`--color-greyscale-600`),
        disabled: interpolateCSSVariable(`--color-greyscale-400`),
      },
      action: {
        active: interpolateCSSVariable('--color-primary-100'),
        disabled: interpolateCSSVariable(`--color-greyscale-400`),
        disabledBackground: interpolateCSSVariable(`--color-greyscale-100`),
        hover: interpolateCSSVariable('--color-greyscale-50'),
      },
      background: {
        default: interpolateCSSVariable('--color-greyscale-100'),
        paper: interpolateCSSVariable(`--color-white`),
      },
    },
    components: {
      MuiCheckbox: {
        styleOverrides: {
          colorPrimary: {
            color: interpolateCSSVariable(`--color-primary-500`),
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            marginLeft: 0,
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            border: `1px solid ${interpolateCSSVariable('--color-greyscale-100')}`,
            borderRadius: interpolateCSSVariable('--border-radius-triple'),
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            height: 'fit-content',
            padding: 0,
            borderRadius: interpolateCSSVariable('--border-radius'),
            '&.MuiChip-filledPrimary': {
              color: interpolateCSSVariable('--color-primary-700'),
              backgroundColor: interpolateCSSVariable('--color-primary-100'),
            },
            '&.MuiChip-filledSecondary': {
              color: interpolateCSSVariable('--color-secondary-700'),
              backgroundColor: interpolateCSSVariable('--color-secondary-50'),
            },
            '&.MuiChip-filledSuccess': {
              color: interpolateCSSVariable('--color-success-700'),
              backgroundColor: interpolateCSSVariable('--color-success-100'),
            },
            '&.MuiChip-filledWarning': {
              color: interpolateCSSVariable('--color-warning-800'),
              backgroundColor: interpolateCSSVariable('--color-warning-50'),
            },
          },
          label: {
            padding: '4px 8px',
          },
        },
        variants: [
          {
            props: { variant: 'rounded' },
            style: {
              label: {
                padding: '3px 4px',
              },
              borderRadius: '25px',
            },
          },
        ],

        defaultProps: {
          sx: {
            fontWeight: 400,
            fontSize: 'var(--font-size-base-sm)',
          },
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            letterSpacing: 'normal',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: 'var(--font-size-base)',
            transform: 'scale(1)',
            fontWeight: 'var(--font-weight-regular)',
          },
        },
      },
      MuiLink: {
        defaultProps: {
          component: LinkBehavior,
        } as LinkProps,
        styleOverrides: {
          underlineHover: {
            paddingBottom: '0',
          },
          root: {
            '&:hover': {
              paddingBottom: '0',
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
          },
        },
      },
      MuiButton: {
        variants: [
          {
            props: { variant: 'squared', color: 'primary' },
            style: { borderRadius: '0' },
          },
          {
            props: { variant: 'ghost' },
            style: ({ theme }) => getBaseGhostProps(theme),
          },
          {
            props: { variant: 'ghost', size: 'small' },
            style: ({ theme }) => ({
              ...getBaseGhostProps(theme),
              padding: '3px 9px',
              fontSize: '14px',
            }),
          },
          {
            props: { variant: 'ghost', color: 'warning' },
            style: ({ theme }) => ({
              ...getBaseGhostProps(theme, 'warning'),
              border: '1px solid #FFA068',
              backgroundColor: '#FFF5E8',
              color: '#DB5327',
            }),
          },
        ],

        styleOverrides: {
          root: ({ ownerState, theme }) => {
            const { color, variant = 'primary', disabled } = ownerState;

            const baseButton = {
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'none',
              ':hover': {
                boxShadow: '0px 2px 4px 0px #E4E4E7, 0px 5px 25px 0px rgba(228, 228, 231, 0.25)',
              },
              svg: {
                fill: 'currentColor',
              },
            };

            const isSecondaryOutlinedMode = color === 'secondary' && variant === 'outlined';
            if (isSecondaryOutlinedMode) {
              return disabled ? secondary_action_button_disabled : secondary_action_button;
            }

            const isBasicOutlinedMode = variant === 'outlined' && color === 'info';
            if (isBasicOutlinedMode) {
              return disabled ? basic_action_button_disabled : basic_action_button;
            }

            if (ownerState.variant === 'squared') {
              return {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.common.white,
                borderRadius: '0px',
              };
            }

            return baseButton;
          },
          text: {
            color: interpolateCSSVariable('--color-primary-600'),
            backgroundColor: 'transparent',
            ':hover': {
              textDecoration: 'underline',
              boxShadow: 'none',
              backgroundColor: 'transparent',
            },
          },
        },
        defaultProps: {
          LinkComponent: LinkBehavior,
          sx: {
            fontSize: 'var(--font-size-base)',
            textTransform: 'none',
          },
        },
      },
      MuiSkeleton: {
        defaultProps: {
          animation: 'wave',
        },
      },
      MuiTooltip: {
        defaultProps: {
          enterDelay: 300,
          enterNextDelay: 300,
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: 'var(--font-size-base)',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            svg: {
              fill: 'currentColor',
            },
          },
        },
        defaultProps: {
          sx: {
            color: 'rgba(0, 0, 0, 0.54)',
            fontSize: '2.4rem',
          },
        },
      },
      MuiTypography: {
        variants: [
          {
            props: { variant: 'tdValue' },
            style: {
              fontWeight: '500',
              fontSize: '14px',
              color: 'var(--color-greyscale-800)',
            },
          },
        ],

        defaultProps: {
          variantMapping: {
            button2: 'p',
            body2: 'span',
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          sizeSmall: {
            ...body2FontProps,
          },
          inputSizeSmall: {
            padding: '6px 10px',
          },
        },
      },
      MuiTextField: {
        variants: [
          {
            props: { size: 'x-small' },
            style: {
              '& .MuiInputBase-root': {
                fontSize: '14px',
                lineHeight: '18px',
              },
              '& .MuiInputBase-input': {
                padding: '4px 8px',
                height: '24px',
              },
              '& .MuiOutlinedInput-root': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: interpolateCSSVariable('--border-radius'),
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '12px',
                lineHeight: '16px',
              },
            },
          },
        ],
      },
      MuiAlert: {
        styleOverrides: {
          standardInfo: {
            '& .MuiAlert-icon': {
              color: interpolateCSSVariable('--color-greyscale-400'),
            },
          },
        },
      },
    },
  });
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    body3: CSSProperties;
    bodySmall: CSSProperties;
    bodyLarge: CSSProperties;
    bodyDefault: CSSProperties;
    subheading2: CSSProperties;
    button2: CSSProperties;
    longForm: CSSProperties;
    menu: CSSProperties;
    menu2: CSSProperties;
    subheading: CSSProperties;
    longform: CSSProperties;
    tiny: CSSProperties;
    bold: CSSProperties;
  }

  interface Palette {
    neutral: Palette['primary'];
    basic: Palette['primary'];
    disabled: Palette['primary'];
  }

  interface PaletteOptions {
    neutral: PaletteOptions['primary'];
    basic: PaletteOptions['primary'];
    disabled: PaletteOptions['primary'];
  }

  interface PaletteColor {
    darker?: string;
    light50?: string;
    light100?: string;
    light200?: string;
    grey100?: string;
    grey600?: string;
  }

  interface SimplePaletteColorOptions {
    darker?: string;
    light50?: string;
    light100?: string;
    light200?: string;
    grey100?: string;
    grey600?: string;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    body3?: CSSProperties;
    bodySmall?: CSSProperties;
    bodyLarge?: CSSProperties;
    bodyDefault?: CSSProperties;
    subheading2?: CSSProperties;
    button2?: CSSProperties;
    longForm?: CSSProperties;
    menu?: CSSProperties;
    menu2?: CSSProperties;
    subheading?: CSSProperties;
    longform?: CSSProperties;
    tiny?: CSSProperties;
    bold: CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    button2: true;
    subheading2: true;
    body3: true;
    bodySmall: true;
    bodyLarge: true;
    bodyDefault: true;
    longForm: true;
    menu: true;
    menu2: true;
    tdValue: true;
    subheading: true;
    longform: true;
    tiny: true;
    bold: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    rounded: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    squared: true;
    ghost: true;
    primary: true;
  }

  interface ButtonPropsColorOverrides {
    basic: true;
    neutral: true;
  }
}

interface ButtonPropsColorOverrides {
  basic: true;
  neutral: true;
}

declare module '@mui/material/TextField' {
  interface TextFieldPropsSizeOverrides {
    'x-small': true;
  }
}

export default makeFinalTheme;
